import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography, Chip } from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import { Link, useHistory } from 'react-router-dom';
import { getUserData } from '../../utils/FunctionHelpers';
import { deleteSociete } from '../../services/SocieteService';
import DialogDeleteCustom from '../../components/DialogDeleteCustom/DialogDeleteCustom';
import { withSnackbar } from '../../utils/SnackbarHOC';
import { debounce } from 'lodash';
import { handleChangeRowsPerPage, getRowsPerPage } from '../../utils/DataTables';
import moment from 'moment';
import { commonGridSx, gridContainerSx, gridLocaleText, makeGridToolbar } from '../../utils/gridStyles';
import { DataGrid } from '../../components/SortableDataGrid/SortableDataGrid';

const tableName = 'societeColumnVisibility';
const CustomToolbar = makeGridToolbar('Sociétés');

const ListerSociete = ({ societesData, handleSearchSocietes, parameters, snackbarShowMessage }) => {
  const history = useHistory();
  const mounted = useRef(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [loadToDelete, setLoadToDelete] = useState(false);
  const [societeSelectedToDelete, setSocieteSelectedToDelete] = useState(null);

  const [paginationModel, setPaginationModel] = useState({
    page: (societesData.pageSociete || 1) - 1,
    pageSize: getRowsPerPage(tableName) ?? societesData.limit ?? 50,
  });
  const [sortModel, setSortModel] = useState(
    societesData.sort?.column ? [{ field: societesData.sort.column, sort: societesData.sort.direction }] : []
  );

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const handleCloseDialog = () => { if (!loadToDelete) setOpenDialogDelete(false); };
  const handleOpenDialog = (societe) => {
    setSocieteSelectedToDelete({ id: societe.id, name: societe.name.charAt(0).toUpperCase() + societe.name.slice(1) });
    setOpenDialogDelete(true);
  };

  const handleDeleteSociete = async (uuid) => {
    try {
      setLoadToDelete(true);
      snackbarShowMessage('Patientez svp...', 'warning', null);
      const data = await deleteSociete(uuid);
      handleSearchSocietes(societesData.pageSociete, parameters);
      snackbarShowMessage(data);
    } catch {
      snackbarShowMessage("Erreur à la suppression de la societe", 'error', 4000);
    } finally {
      setLoadToDelete(false);
      setOpenDialogDelete(false);
    }
  };

  const debouncedSearch = debounce((searchText) => {
    handleSearchSocietes(1, {
      ...parameters,
      column: sortModel[0]?.field || '',
      sort: sortModel[0]?.sort || 'asc',
      searchedText: searchText,
    });
  }, 800);

  const handlePaginationModelChange = (newModel) => {
    if (newModel.pageSize !== paginationModel.pageSize) {
      handleChangeRowsPerPage(tableName, newModel.pageSize);
      handleSearchSocietes(1, parameters, newModel.pageSize);
    } else {
      handleSearchSocietes(newModel.page + 1, {
        ...parameters,
        column: sortModel[0]?.field || '',
        sort: sortModel[0]?.sort || 'asc',
      });
    }
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    handleSearchSocietes(paginationModel.page + 1, {
      ...parameters,
      column: newSortModel[0]?.field || '',
      sort: newSortModel[0]?.sort || 'asc',
    });
  };

  const handleFilterModelChange = (filterModel) => {
    debouncedSearch(filterModel.quickFilterValues?.join(' ') || '');
  };

  const rows = (societesData?.societes || []).map((item) => ({
    ...item,
    updatedAt: item?.updatedAt?.date ? item.updatedAt.date : item?.updatedAt,
  }));

  const columns = [
    {
      field: 'nom_societe', headerName: 'NOM SOCIÉTÉ', width: 260,
      renderCell: (p) => (
        <Link to={{ pathname: `/societe/${p.row.uuid}/view`, state: { uuid: p.row.uuid } }}
          style={{ textDecoration: 'none', fontWeight: 600, color: '#0f172a', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value}
        </Link>
      ),
    },
    {
      field: 'updatedAt', headerName: 'DATE MAJ', width: 110,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>
          {p.value && moment(p.value).isValid() ? moment(p.value).format('DD/MM/YYYY') : '—'}
        </Typography>
      ),
    },
    {
      field: 'statut', headerName: 'STATUT', width: 110,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569', textTransform: 'capitalize' }}>{p.value || '—'}</Typography>
      ),
    },
    {
      field: 'typeSociete', headerName: 'TYPE', width: 130,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value?.type ?? '—'}</Typography>
      ),
    },
    {
      field: 'secteur_dactivite', headerName: "SECTEUR D'ACTIVITÉ", width: 180,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value?.secteur ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'siteinternet', headerName: 'INTERNET', width: 160, sortable: false,
      renderCell: (p) => p.value ? (
        <a href={p.value} target="_blank" rel="noreferrer"
          style={{ textDecoration: 'none', color: '#0ea5e9', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value}
        </a>
      ) : <Typography sx={{ color: '#cbd5e1', fontSize: '1rem' }}>—</Typography>,
    },
    {
      field: 'localisation', headerName: 'LOCALISATION', width: 140,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569', textTransform: 'capitalize' }}>{p.value || '—'}</Typography>
      ),
    },
    {
      field: 'contacts', headerName: 'CONTACTS', width: 100, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <Box sx={{ cursor: 'pointer' }} onClick={() => history.push(`/societe/${p.row.uuid}/view?tab=2`)}>
          <Chip label={p.value ?? 0} size="small"
            sx={{ background: '#ebf4fb', color: '#03407b', fontWeight: 600, fontSize: '0.8125rem', cursor: 'pointer' }} />
        </Box>
      ),
    },
    {
      field: 'missions', headerName: 'MISSIONS', width: 100, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <Box sx={{ cursor: 'pointer' }} onClick={() => history.push(`/societe/${p.row.uuid}/view?tab=1`)}>
          <Chip label={p.value ?? 0} size="small"
            sx={{ background: '#f0fdf4', color: '#16a34a', fontWeight: 600, fontSize: '0.8125rem', cursor: 'pointer', border: '1px solid #bbf7d0' }} />
        </Box>
      ),
    },
    {
      field: 'actions', headerName: 'ACTIONS', width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {getUserData()?.role?.includes('ROLE_DIRECTION') && (
            <Tooltip title="Supprimer">
              <IconButton size="small"
                onClick={() => handleOpenDialog({ id: p.row.uuid, name: p.row.nom_societe })}
                sx={{ color: '#ef4444', background: '#fef2f2', borderRadius: '8px', p: 0.75, '&:hover': { background: '#fee2e2' } }}>
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <Box sx={gridContainerSx}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.uuid}
          loading={societesData.isLoading}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          rowCount={societesData.societesCount ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[50, 100]}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onFilterModelChange={handleFilterModelChange}
          disableRowSelectionOnClick
          showToolbar
          slots={{ toolbar: CustomToolbar }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          localeText={gridLocaleText}
          sx={commonGridSx}
        />
      </Box>
      <DialogDeleteCustom
        handleClose={handleCloseDialog}
        open={openDialogDelete}
        loading={loadToDelete}
        title="Supprimer la société"
        handleConfirm={() => handleDeleteSociete(societeSelectedToDelete?.id)}
        label={`Voulez-vous vraiment supprimer la société ${societeSelectedToDelete?.name} ?`}
      />
    </>
  );
};

export default withSnackbar(ListerSociete);


